import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { logoutUser } from '../../store/user/userV2/actions';
import { clearLocalStorageOnLogout } from '../../utils/utils';
import LogoutIcon from '../../assets/Svg/Logouticon';
import CrossIcon from '../../assets/Svg/CrossIcon';
import TravelExplore from '../../assets/Svg/TravelExplore';
import useSearchResultFetcher from '../GlobalSearch/useSearchResultFetcher';
import { Modal, Button } from '../_custom';
import { circlePlusIconLightBlack } from '../../assets/hostedassets';
import AddToWatchlistDropdown from '../../modules/Investor/Portfolio/components/AddToWatchlist/AddToWatchlistDropdown';
import NotificationBell from '../NotificationCenter/NotificationBell';
import { useNotificationBellEnabled } from '../NotificationCenter/useNotificationBellEnabled';
import { Mixpanel } from '../../utils/mixpanel';
import { AICA_ADD_LEAD } from '../../utils/constants/mixpanelEvents/investorEvents';
import styles from './MobileHeader.module.scss';

/**
 * Briefly flashes a purple shadow/lift highlight on a newly added card so the user's eye
 * is drawn to it, then resets the inline styles after 1.5s. The reset timeout is tracked
 * via `timers` so callers can clear it on unmount (see highlightCard's cleanup effect).
 */
const flashHighlightCard = (card: HTMLElement, timers: React.MutableRefObject<ReturnType<typeof setTimeout>[]>) => {
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    card.style.boxShadow = '0 4px 16px rgba(96, 33, 179, 0.2)';
    card.style.transform = 'translateY(-2px)';
    card.style.transition = 'box-shadow 0.3s ease, transform 0.3s ease';
    card.style.borderRadius = '0.75rem';
    const resetTid = setTimeout(() => {
        card.style.boxShadow = '';
        card.style.transform = '';
        card.style.transition = '';
        card.style.borderRadius = '';
    }, 1500);
    timers.current.push(resetTid);
};

/**
 * Recursively polls the DOM for a card matching orgName (cards render async after
 * the add-new-lead flow completes) and flashes it once found. Gives up after `attempts`.
 */
const tryFindAndHighlightCard = (orgName: string, attempts: number, timers: React.MutableRefObject<ReturnType<typeof setTimeout>[]>) => {
    const tid = setTimeout(() => {
        const allCards = document.querySelectorAll('[data-org-name]');
        let card: HTMLElement | null = null;
        for (const c of Array.from(allCards)) {
            if ((c as HTMLElement).dataset.orgName === orgName) {
                card = c as HTMLElement;
                break;
            }
        }
        if (card) {
            flashHighlightCard(card, timers);
        } else if (attempts > 0) {
            tryFindAndHighlightCard(orgName, attempts - 1, timers);
        }
    }, 500);
    timers.current.push(tid);
};

const MobileHeader = () => {
    const domainMetadata = useAppSelector((state) => state.user.domainMetadata);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [showSearch, setShowSearch] = useState<boolean>(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState<boolean>(false);

    const notificationBellEnabled = useNotificationBellEnabled();
    const inputRef = useRef<HTMLInputElement>(null);
    const highlightTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

    const { setSearchText, searchText, searchResults, navigateOrAddLead, setSearchResults, handleAddNewFlow, searchLoading } =
        useSearchResultFetcher();

    useEffect(() => {
        if (showSearch) {
            // Immediate focus attempt + delayed retry for mobile keyboard activation
            inputRef.current?.focus();
            setTimeout(() => inputRef.current?.focus(), 50);
        } else {
            setSearchText('');
            setSearchResults(null);
        }
    }, [showSearch]);

    // Clear all pending highlight timers on unmount
    useEffect(() => {
        return () => {
            highlightTimers.current.forEach(clearTimeout);
        };
    }, []);

    const highlightCard = useCallback((orgName: string) => {
        // Clear any previous highlight timers
        highlightTimers.current.forEach(clearTimeout);
        highlightTimers.current = [];

        tryFindAndHighlightCard(orgName, 8, highlightTimers);
    }, []);

    const handleSelectAlreadyAdded = async (result: any) => {
        setShowSearch(false);
        // Already-added company → open its AICA report (same navigation the desktop search uses).
        const accessType = result.companyAccessType || '';
        navigateOrAddLead(result.orgId, accessType, { ...result, companyAccessType: accessType }, searchText);
    };

    const handleSelectAddNew = async (result: any) => {
        const accessType = result.companyAccessType || '';
        const resultWithGroup = { ...result, companyAccessType: accessType };
        const orgName = result.orgName || searchText;
        setShowSearch(false);
        await handleAddNewFlow({
            baseResult: resultWithGroup,
            accessType,
            searchTextValue: searchText,
            initialPrefillLoadingFields: { website: true, brand: true },
            overwriteIdentifierFromCompanyInfo: true,
        });
        // After modal flow completes and list refreshes, highlight the new card
        highlightCard(orgName);
    };

    const handleFooterAddNew = async () => {
        const orgName = searchText;
        setShowSearch(false);
        const syntheticResult = {
            orgId: null,
            orgName: searchText,
            websiteUrl: '',
        };
        await handleAddNewFlow({
            baseResult: syntheticResult,
            accessType: '',
            searchTextValue: searchText,
            initialPrefillLoadingFields: { identifier: true, website: true, brand: true },
            overwriteIdentifierFromCompanyInfo: true,
            isAddUsingSearchText: true,
        });
        // After modal flow completes and list refreshes, highlight the new card
        highlightCard(orgName);
    };

    const handleLogout = () => {
        dispatch(
            logoutUser(null, {
                onSuccess: () => {},
            }),
        );
        navigate('/investor');
        clearLocalStorageOnLogout();
    };

    return (
        <>
            <div className={styles.Header}>
                <div className={styles.LogoContainer}>
                    {domainMetadata?.mainImageUrl ? (
                        <img className={styles.Logo} src={domainMetadata.mainImageUrl} alt={domainMetadata?.orgName || 'Logo'} />
                    ) : (
                        <span className={styles.LogoFallback}>RECUR</span>
                    )}
                </div>

                <div className={styles.HeaderActions}>
                    <Button
                        text=""
                        className={styles.ActionButton}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: 0,
                            marginTop: 0,
                            border: 'none',
                            background: 'none',
                            borderRadius: '50%',
                            gap: 0,
                        }}
                        icon={<img src={circlePlusIconLightBlack} alt="add" width="24" height="24" />}
                        iconStyles={{ height: '24px', width: '24px' }}
                        onClick={() => {
                            Mixpanel.track(AICA_ADD_LEAD, { source: 'mobile_header' });
                            setShowSearch(true);
                        }}
                    />
                    <NotificationBell enabled={notificationBellEnabled} />
                    <button className={styles.LogoutButton} onClick={() => setShowLogoutConfirm(true)}>
                        <LogoutIcon color="var(--icon-color)" width="20" height="20" />
                    </button>
                </div>
            </div>

            {showSearch && (
                <div className={styles.SearchOverlay}>
                    <div className={styles.SearchBar}>
                        <span className={styles.SearchBarIcon}>
                            <TravelExplore height={18} width={18} />
                        </span>
                        <input
                            ref={inputRef}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            type="text"
                            autoFocus
                            placeholder="Enter Name, CIN, PAN, GSTIN or website"
                            className={styles.SearchInput}
                        />
                        <span className={styles.SearchClose} onClick={() => setShowSearch(false)}>
                            {searchLoading ? (
                                <span className={styles.SearchSpinner} />
                            ) : (
                                <CrossIcon height="12px" width="12px" colour="#6021b3" />
                            )}
                        </span>
                    </div>
                    <AddToWatchlistDropdown
                        visible
                        searchText={searchText}
                        searchResults={searchResults}
                        wrapperClassName={styles.SearchDropdown}
                        onSelectAlreadyAdded={handleSelectAlreadyAdded}
                        onSelectAddNew={handleSelectAddNew}
                        footerVisible={searchText.length >= 3}
                        footerLabel={`Add using "${searchText}"`}
                        onFooterAddNew={handleFooterAddNew}
                    />
                    <div className={styles.SearchBackdrop} onClick={() => setShowSearch(false)} />
                </div>
            )}

            {/* Logout confirmation — same pattern as the Archive Lead confirm modal */}
            <Modal open={showLogoutConfirm} onClose={() => setShowLogoutConfirm(false)} size="flexible" className={styles.LogoutModal}>
                <div className={styles.LogoutModalHeader}>
                    <div className={styles.LogoutModalTitle}>Log Out</div>
                </div>
                <div className={styles.LogoutModalBody}>Are you sure you want to log out?</div>
                <div className={styles.LogoutModalFooter}>
                    <Button text="Cancel" onClick={() => setShowLogoutConfirm(false)} variant="secondary" />
                    <Button
                        text="Log Out"
                        onClick={() => {
                            setShowLogoutConfirm(false);
                            handleLogout();
                        }}
                    />
                </div>
            </Modal>
        </>
    );
};

export default MobileHeader;
